import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Chart from 'react-apexcharts';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react/dist/iconify.js';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Popup for a single status: a bar chart of complaint counts per DIVISION
// (labelled with its parent circle + zone). Click a bar to list that division's
// complaints of this status; click a complaint to open its report.
const StatusDivisionGraphModal = ({ show, onHide, status, title, color = '#6366f1' }) => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('chart'); // 'chart' | 'list'
  const [division, setDivision] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loadingComplaints, setLoadingComplaints] = useState(false);

  const fetchBreakdown = useCallback(async () => {
    setLoading(true); setError(null); setView('chart'); setDivision(null); setComplaints([]);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/backend/complaints/status-division-breakdown`, {
        params: { status },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status) setBreakdown(res.data.data || []);
      else setError(res.data.message || 'Failed to load.');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, status]);

  useEffect(() => {
    if (show && status) fetchBreakdown();
  }, [show, status, fetchBreakdown]);

  const openDivision = async (d) => {
    setDivision(d);
    setView('list');
    setLoadingComplaints(true);
    setComplaints([]);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/backend/complaints/complaints-by-forest-node`, {
        params: { level: 'division', id: d.divisionId, status, perPage: 300, pageNo: 1 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setComplaints(res.data.data || []);
    } catch (err) {
      setComplaints([]);
    } finally {
      setLoadingComplaints(false);
    }
  };

  const openComplaint = (id) => { window.open(`/complains/view/${id}`, '_blank', 'noopener'); };

  // Chart config
  const labels = breakdown.map((d) => [d.divisionName, `${d.circleName || '-'} · ${d.zoneName || '-'}`]);
  const values = breakdown.map((d) => d.count);
  const chartHeight = Math.max(300, breakdown.length * 40 + 40);

  const options = {
    chart: {
      type: 'bar', toolbar: { show: false }, animations: { enabled: true, speed: 400 }, fontFamily: 'inherit',
      events: {
        dataPointSelection: (_e, _c, cfg) => {
          const d = breakdown[cfg.dataPointIndex];
          if (d) openDivision(d);
        },
      },
    },
    plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: '65%' } },
    colors: [color],
    dataLabels: { enabled: true, style: { colors: ['#fff'], fontWeight: 700 }, formatter: (v) => Math.round(v), offsetX: 4 },
    grid: { borderColor: '#eef2f6' },
    xaxis: { categories: labels, labels: { style: { fontSize: '11px', colors: '#94a3b8' } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { fontSize: '11.5px', fontWeight: 600, colors: '#334155' }, maxWidth: 260 } },
    legend: { show: false },
    states: { hover: { filter: { type: 'darken', value: 0.12 } } },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const d = breakdown[dataPointIndex];
        if (!d) return '';
        return `<div style="padding:9px 13px;font-size:12.5px;line-height:1.5;">` +
          `<div style="font-weight:800;">${d.divisionName}</div>` +
          `<div>Circle: ${d.circleName || '-'}</div>` +
          `<div>Zone: ${d.zoneName || '-'}</div>` +
          `<div style="margin-top:3px;"><b>${d.count}</b> ${title || ''} complaint(s) · click to view</div></div>`;
      },
    },
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" fullscreen="lg-down" dialogClassName="sdg-modal-wide">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: 18 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: 'inline-block' }} />
          {title} — by Division
          {view === 'list' && division && (
            <span className="text-secondary-light" style={{ fontSize: 14 }}>
              › {division.divisionName} <span style={{ fontSize: 12 }}>({division.circleName} · {division.zoneName})</span>
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '76vh', overflowY: 'auto' }}>
        {loading ? (
          <Skeleton height={360} />
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : view === 'chart' ? (
          breakdown.length === 0 ? (
            <div className="text-center text-secondary-light py-5">
              <Icon icon="mdi:chart-bar" width="44" /><div>No {title} complaints assigned to any division.</div>
            </div>
          ) : (
            <>
              <div className="text-secondary-light mb-2" style={{ fontSize: 13 }}>
                Each bar is a division (with its circle · zone). Click a bar to see its {title} complaints.
              </div>
              <Chart options={options} series={[{ name: title, data: values }]} type="bar" height={chartHeight} />
            </>
          )
        ) : (
          // List view for the selected division
          <div>
            <Button variant="outline-secondary" size="sm" className="mb-3 d-flex align-items-center gap-1" onClick={() => setView('chart')}>
              <Icon icon="mdi:arrow-left" /> Back to divisions
            </Button>
            {loadingComplaints ? (
              <Skeleton count={5} height={54} />
            ) : complaints.length === 0 ? (
              <div className="text-center text-secondary-light py-5">No {title} complaints in this division.</div>
            ) : (
              <div className="table-responsive">
                <table className="table bordered-table mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Complaint #</th><th>Date</th><th>Nature</th><th>Referred By</th><th>Complainant</th><th>Priority</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {complaints.map((c) => (
                      <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => openComplaint(c._id)}>
                        <td className="fw-semibold" style={{ color }}>{c.complaint_number}</td>
                        <td>{c.date ? new Date(c.date).toLocaleDateString() : '-'}</td>
                        <td>{c.nature || '-'}</td>
                        <td>{c.referred_by || '-'}</td>
                        <td>{c.complainant_name || '-'}</td>
                        <td>{c.priority || '-'}</td>
                        <td><Icon icon="mdi:open-in-new" style={{ color }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StatusDivisionGraphModal;
